import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { fetcher } from '../../util/axios';
import { Loader } from 'lucide-react';
import { useParams, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import dayjs from 'dayjs';
import placeholder from '../../util/placeholder';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { allowOnlyNumbers } from '../../util/helpers';
import { Label } from '../../components/ui/label';
import requestErrorHandler from '../../util/requestErrorHandler';
import { handleMutation, handleUpdates } from "../../util/axios";
import { message } from 'antd';
import useSWRMutation from "swr/mutation";
import { useCurrency } from '../../hooks/useCurrency';

function Rental() {
  const [acceptOrder, setAcceptOrder] = useState(false)
  const [selectedVehicles, setSelectedVehicles] = useState<{
    vehicleId: string;
    tripCount: number;
  }[]>([]);
  // const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  // const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const params = useParams();
  const [searchParams] = useSearchParams()
  const [ formatCurrency ] = useCurrency();

  const { data: assignment, error, isLoading, mutate } = useSWR({url: `fleet/assignments/${params?.id}`}, fetcher);

  const isAcceptedAssignment = assignment?.data?.status === 'Accepted';
  const isCompletedAssignment = assignment?.data?.status === 'Completed';
  const isAssignedAssignment = assignment?.data?.status === 'Assigned';
  const isReassignedAssignment = assignment?.data?.status === 'Reassigned';

  const { trigger, isMutating } = useSWRMutation(
    "fleet/assign-vehicle",
    handleMutation
  );

  const { trigger: triggerCompleteRental, isMutating: isCompletingRental } = useSWRMutation(
    "/fleet/assignments/complete-all",
    handleUpdates
  );

  const { trigger: triggerCompleteTrip, isMutating: isCompletingTrip } = useSWRMutation(
    "fleet/vehicle-assignments/complete",
    handleUpdates
  );

  const { trigger: triggerArchiveRental, isMutating: isArchivingRental } = useSWRMutation(
    "/fleet/assignments/archive",
    handleUpdates
  );

  const { trigger: triggerReject, isMutating: isRejecting } = useSWRMutation(
    "fleet/reject-assignment",
    handleMutation
  );

  const { data: trucksData } = useSWR({url: `fleet/vehicles/available/?truckCategoryId=${searchParams.get('categoryId')}`}, fetcher);

  const details: RentalRequest = useMemo(() => {
    return !isLoading && !error
      ? {
        ...assignment.data.request,
        assignedValue: assignment.data.assignedValue,
        assignedBy: assignment.data.assignedBy
      }
      : null
  }, [isLoading, assignment, error]);

  const vehicleAssignments: VehicleAssignment[] = useMemo(() => {
    return assignment?.data?.vehicleAssignments || []
  }, [assignment])

  const filteredVehicles: Vehicle[] = useMemo(() => {
    return trucksData?.data?.vehicles.filter((vehicle: any) => vehicle.regPlate.toLowerCase().includes(searchQuery.toLowerCase())) || []
  }, [trucksData, searchQuery]);

  const handleTripCountChange = useCallback((newCount: number, selectedId: string) => {
    if (newCount) {
      const found = selectedVehicles.find(v => v.vehicleId === selectedId)
      if (found) {
        setSelectedVehicles((prev) => prev.filter(v => v.vehicleId !== selectedId))
      }
      const newVehicle = {
        vehicleId: selectedId,
        tripCount: newCount
      }
      setSelectedVehicles(prev => [...prev, newVehicle])
    } else {
      const found = selectedVehicles.find(v => v.vehicleId === selectedId)
      if (found) {
        setSelectedVehicles((prev) => prev.filter(v => v.vehicleId !== selectedId))
      }
    }
  }, [selectedVehicles]);

  const handleConfirm = async () => {
    try {
      const res = await trigger({
        renterRequestAssignmentId: params.id,
        truckCategoryId: details.truckCategoryId,
        assignedVehicles: selectedVehicles.map((vehicle) => ({
          vehicleId: vehicle.vehicleId,
          noOfTrips: vehicle.tripCount
        }))
      });
      mutate();
      message.success(res.message);
      setAcceptOrder(false);
      setSelectedVehicles([]);
    } catch (error) {
      requestErrorHandler(error);
    }
  }

  const handleReject = async () => {
    try {
      const res = await triggerReject({
        assignmentRequestId: params?.id
      });
      mutate();
      message.success(res.message);
    } catch (error) {
      requestErrorHandler(error);
    }
  }

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }

  const completeTrip = async (completedTrips: number, vehicleAssignmentId: string) => {
    try {
      const res = await triggerCompleteTrip({
        completedTrips,
        vehicleAssignmentId
      });
      mutate();
      message.success(res.message);
    } catch (error) {
      requestErrorHandler(error);
    }
  }
  const completeRental = async () => {
    try {
      const res = await triggerCompleteRental({
        requestId: params?.id
      });
      mutate();
      message.success(res.message);
    } catch (error) {
      requestErrorHandler(error);
    }
  }

  const archiveRental = async () => {
    try {
      const res = await triggerArchiveRental({
        assignmentId: assignment?.data?.id
      });
      mutate();
      message.success(res.message);
    } catch (error) {
      requestErrorHandler(error);
    }
  }

  if (isLoading) {
    return (
      <div className="w-full flex justify-center items-center p-6">
        <Loader className="animate-spin text-black" />
      </div>);
  }

  if (error) {
    return (<div className="h-[400px] flex items-center justify-center text-gray-500">
      <p>Error loading rental details. Please try again later.</p>
    </div>)
  }

  return (
    <div className="p-6 flex flex-col h-full">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div className="flex items-center gap-3">
          <img
            src={details?.truckCategory?.image || placeholder}
            alt="material"
            className="w-12 h-12 rounded object-cover border border-blue-300"
          />
          <div>
            <div className="font-semibold text-lg capitalize">{details.material}</div>
            <div className="text-xs text-gray-400">Material</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left: Rental Details */}
        <div>          
          <div className="bg-[#F8FAFC] rounded-md p-5 border border-[#6e6f71]">
            <div className="text-xs text-gray-500 mb-1">Date of operation: <span className="font-medium mx-4">{dayjs(details.deliveryDate).format('DD/MM/YYYY hh:mmA')}</span></div>
            <div className="text-xs text-gray-500 mb-1">Trips requested: <span className="text-[#FF8A00] font-medium mx-4">{assignment?.data?.tripCount}</span></div>
            <div className="text-xs text-gray-500 mb-1">Rental value:{' '} <span className="text-[#FF8A00] font-medium mx-4">{formatCurrency(details?.assignedValue)}</span></div>
            <div className="text-xs text-gray-500 mb-1">
              Assigned by:
              <span className="text-[#007AFF] font-medium cursor-pointer mx-4">{details.assignedBy?.name || 'Not Assigned'}</span>
              {' '}
              {details.assignedBy?.createdAt && <span className="mx-4">{dayjs(details.assignedBy?.createdAt).format('DD/MM/YYYY  @  hh:mmA')}</span>}
            </div>

            {/* Pickup/Dropoff */}
            <div className="mt-6">
              <div className="font-semibold text-sm mb-2">Pickup</div>
              <div className="bg-[#E5F0FF] rounded p-3 mb-3">
                <div className="font-medium text-xs text-[#007AFF]">{details?.pickupAddress}</div>
                {/* <div className="text-xs text-gray-500">{details.pickupZone.description}</div> */}
              </div>
              <div className="font-semibold text-sm mb-2">DropOff</div>
              <div className="bg-[#E5F0FF] rounded p-3">
                <div className="font-medium text-xs text-[#007AFF]">{details?.dropoffAddress}</div>
                {/* <div className="text-xs text-gray-500">{details.dropoffZone.description}</div> */}
              </div>
            </div>
            {/* Accept/Reject */}
            {(isAssignedAssignment || isReassignedAssignment) && !acceptOrder && (
              <div className="mt-4 flex items-center gap-4">
                  <Button
                    variant="secondary"
                    className="flex-1"
                    onClick={handleReject}
                  >
                    {isRejecting ? <Loader /> : "Reject"}
                  </Button>
                  <Button className="w-3/5" onClick={() => setAcceptOrder(true)}>
                    Accept
                  </Button>
              </div>
            )}
          </div>
        </div>

        {/* Right: Trips/Progress */}
        <div className="flex flex-col gap-4">
            {/* List Vehicle to assign */}
            {acceptOrder && (<div className="space-y-8">
              <div className="space-y-1">
                  <Label htmlFor="search" className="text-gray-500 font-semibold">
                    Select Vehicle and enter number of trips
                  </Label>
                  <Input
                    id="search"
                    placeholder="Search"
                    className="text-gray-500 w-full ml-auto border-x-0 border-t-0 outline-none border-b-2 border-gray-400"
                    onChange={handleSearchChange}
                  />
              </div>
              <div className="space-y-3">
                {filteredVehicles.map((vehicle, index) => (
                  <Vehicle
                    key={index + vehicle.id}
                    details={{
                      id: vehicle.id,
                      image: vehicle.truckCategoryDetails.image,
                      licensePlate: vehicle.regPlate,
                      trips: vehicle.totalTrips
                    }}
                    onChange={handleTripCountChange}
                  />
                ))}
              </div>
              <Button
                className="w-full"
                disabled={!selectedVehicles.length || isMutating}
                onClick={handleConfirm}
                
              >
                {isMutating ? <Loader /> : 'Confirm'}
              </Button>
            </div>)}
            {isAcceptedAssignment && (
              <>
                {/* Trip Cards */}
                {vehicleAssignments.map((assignment) => (
                  <TripSummary
                    key={assignment.id}
                    summary={{
                      id: assignment.vehicle.id,
                      image: details.truckCategory.image,
                      totalTrips: assignment.noOfTrips,
                      completedTrips: assignment.completedTrips,
                      lastTripDate: assignment.updatedAt,
                      licensePlate: assignment.vehicle.regPlate
                    }}
                    onComplete={(currentTrip) => completeTrip(currentTrip, assignment.id)}
                    isCompletingTrip={isCompletingTrip}
                  />)
                )}

                {/* Complete Rental Button */}
                <div className="md:flex justify-end mt-8">
                  <Button
                    disabled={isCompletingRental}
                    className="bg-[#007AFF] text-white px-8 py-3 rounded font-semibold text-sm hover:bg-[#005bb5] w-full md:w-auto"
                    onClick={completeRental}
                  >
                    {isCompletingRental ? <Loader /> : 'Complete Rental'}
                  </Button>
                </div>
              </>
            )}
            {isCompletedAssignment && (
              <>
                {/* Completed Trip Cards */}
                {vehicleAssignments.map((assignment) => (
                  <TripSummary
                    key={assignment.id}
                    summary={{
                      id: assignment.vehicle.id,
                      image: details.truckCategory.image,
                      totalTrips: assignment.noOfTrips,
                      completedTrips: assignment.completedTrips,
                      lastTripDate: assignment.updatedAt,
                      licensePlate: assignment.vehicle.regPlate
                    }}
                  />)
                )}
                {/* Complete Rental Button */}
                <div className="flex justify-end mt-8">
                  <Button
                    className="bg-[#007AFF] text-white px-8 py-3 rounded font-semibold text-sm hover:bg-[#005bb5]"
                    disabled={isArchivingRental}
                    onClick={archiveRental}
                  >
                    {isArchivingRental ? <Loader /> : 'Archive Rental'}
                  </Button>
                </div>
              </>
            )}
        </div>
      </div>
    </div>
  )
}

interface TripSummaryProps {
  summary: {
    id: string;
    image: string;
    totalTrips: number;
    completedTrips: number;
    lastTripDate: string;
    licensePlate: string;
  };
  onComplete?: (trip: number) => void;
  isCompletingTrip?: boolean;
}

const TripSummary = ({ summary, onComplete, isCompletingTrip }: TripSummaryProps) => {
  return (
    <div className="flex flex-col gap-4">
      <div className="bg-blue-50/70 rounded-sm p-4 flex flex-col gap-2 shadow-sm">
        <Vehicle
          details={{
            id: summary.id,
            image: summary.image,
            licensePlate: summary.licensePlate || "VEHICLE-LICENSE-PLATE",
            lastTripDate: summary.lastTripDate,
            trips: summary.totalTrips
          }}
          readonly
        />
        <div className="mt-2">
          <div className="flex items-center justify-between text-blue-500 text-xs mb-1">
              <span>Trip Progress</span>
              <span>{`${summary.completedTrips}/${summary.totalTrips}`}</span>
          </div>
          <div className="w-full bg-[#E5F0FF] rounded-full h-2 mb-2 font-semibold">
            <div className="bg-[#007AFF] h-2 rounded-full" style={{ width: `${(summary.completedTrips / summary.totalTrips) * 100}%` }} />
          </div>
          <div className="flex items-center justify-between text-xs">
            <span>Trip {summary.completedTrips + 1} in progress</span>
            <Button
              className="w-1/2 bg-[#007AFF] text-white px-8 py-3 rounded font-semibold text-sm hover:bg-[#005bb5]"
              onClick={() => onComplete?.(summary.completedTrips + 1)}
              disabled={summary.completedTrips >= summary.totalTrips || isCompletingTrip}
            >
              {isCompletingTrip ? <Loader /> : `Complete Trip ${summary.completedTrips >= summary.totalTrips ? summary.completedTrips : summary.completedTrips + 1}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface VehicleProps {
  details: {
    id: string;
    image: string;
    licensePlate: string;
    lastTripDate?: string;
    trips?: number;
  },
  readonly?: boolean;
  onChange?: (val: number, id: string) => void;
}

const Vehicle = ({details, readonly, onChange
}: VehicleProps) => {
  const [input, setInput] = useState<undefined | number>(undefined)
  const [checked, setChecked] = useState(false);

  const handleCheckChange = (val: boolean) => {
      setChecked(val)
      if (!val) {
        onChange?.(0, details.id);
        setInput(0)
      }
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange?.(Number(e.target.value), details.id);
    setInput(Number(e.target.value))
  }
  return (
    <div className="bg-white p-2 flex items-center gap-2 rounded-sm shadow">
      {!readonly && (
        <Checkbox
          onCheckedChange={handleCheckChange}
          className="h-4 w-4 rounded-sm border-blue-300 text-blue-500 focus:ring-blue-500 mr-2"
        />
      )}
      <img
        src={details.image || placeholder}
        alt="material"
        className="w-8 h-8 rounded object-cover border border-blue-300"
      />
        <div>
          <div className="font-semibold text-sm">{details.licensePlate}</div>
          {details.lastTripDate && (
            <div className="text-xs text-gray-500">
              Last Trip: {dayjs(details.lastTripDate).format('MMM D, YYYY')}
            </div>
          )}
        </div>
        {readonly
            ? <div className="bg-[#E5F0FF] text-[#007AFF] px-3 py-1 text-xs font-bold ml-auto rounded-sm">{details.trips}</div>
            : <Input
                type="text"
                placeholder="0"
                inputMode="numeric"
                className="bg-[#E5F0FF] text-[#007AFF] w-10 h-8 ml-auto border-none outline-none ring-1 ring-blue-400 rounded-sm"
                value={input}
                disabled={!checked}
                onKeyDown={allowOnlyNumbers}
                onChange={handleChange}
              />
        }
    </div>
  )
}

export default Rental;
